import React, {useCallback, useMemo, useState} from 'react'
import {userSelectors} from '../../../../../store/user/userSelectors'
import styles from './IdeationSetup.module.scss'
import {useParams} from 'react-router'
import {useIdeationScope} from '../../../context/IdeationScope'
import {FileUploader} from 'react-drag-drop-files'
import {
  WuButton,
  WuIcon,
  WuInput,
  WuTextarea,
} from '@npm-questionpro/wick-ui-lib'
import {IdeationSetupIdea} from '../IdeationSetupIdea/IdeationSetupIdea'
import {CustomSwitch} from '../../../../../components/CustomSwitch/CustomSwitch'
import {ErrorLabel} from '../../../../../components/ErrorLabel/ErrorLabel'
import {CustomFileUploader} from '../../../../../components/CustomFileUplader/CustomFileUploader'
import {
  DropdownComponent,
  type IDropdownItem,
} from '../../../../../components/DropdownComponent'
import {LoadingPage} from '../../../../LoadingPage'
import {ideationApi} from '../../../../../api/ideationApi'
import {debeounced400} from '../../../../../utils/debounceUtil'
import {uploadDocumentToAWS} from '../../../../../utils/evaporateUtil'
import {AWS_FILE_URL} from '../../../../../constants/apiConstants'
import {IDEATIONSTATUS} from '../../../../../constants/ideationConstants'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {IDEATION} from '../../../../../Translation/English/ideation'
import {GLOBAL} from '../../../../../Translation/English/global'

/** The setup form's own editable-draft shape — stricter than the shared
 * IIdea (text/username/id are always populated once editable), plus a
 * UI-only autofocus flag that has no server-side meaning. */
interface ISetupIdea {
  text: string
  username: string
  id: string
  focus?: boolean
}

export const IdeationSetup: React.FC = () => {
  const {initID} = useParams<{initID: string}>()
  const {ideationId} = useIdeationScope()
  const {data: ideation} = ideationApi.useIdeation(ideationId)
  const user = userSelectors.useCurrentUser()
  const updateIdeationMutation = ideationApi.useUpdateIdeation()
  const createIdeationQuestionMutation = ideationApi.useCreateIdeationQuestion()
  const createIdeationIdeaMutation = ideationApi.useCreateIdeationIdea()
  const wt = useWt()
  const userFirstName = user?.firstname
  const userLastName = user?.lastname

  const votes = useMemo<IDropdownItem[]>(
    () => [
      {id: 1, value: 1},
      {id: 2, value: 2},
      {id: 3, value: 3},
      {id: 4, value: 4},
      {id: 5, value: 5},
      {id: 6, value: 6},
      {id: 7, value: 7},
      {id: 8, value: 8},
      {id: 9, value: 9},
      {id: 10, value: 10},
    ],
    [],
  )

  const [allowIdeas, setAllowIdeas] = useState<boolean>(
    ideation?.allowIdeas ?? false,
  )

  const [allowDefaultIdeas, setAllowDefaultIdeas] = useState<boolean>(
    ideation?.allowDefaultIdeas ?? false,
  )
  const [allowIdeasComments, setAllowIdeasComments] = useState<boolean>(
    ideation?.allowIdeasComments ?? false,
  )

  const [title, setTitle] = useState<string>(ideation?.name ?? '')
  const [introduction, setIntroduction] = useState<string>(
    ideation?.introduction ?? '',
  )
  const [question, setQuestion] = useState<string>(
    ideation?.questions?.[0]?.question ?? '',
  )
  const [ideas, setIdeas] = useState<ISetupIdea[]>(
    ideation?.defaultIdeas?.length
      ? ideation.defaultIdeas.map(idea => ({
          text: idea.text ?? '',
          username: idea.username ?? `${userFirstName}${userLastName}`,
          id: String(idea.id),
        }))
      : [
          {
            text: '',
            username: `${userFirstName}${userLastName}`,
            id: crypto.randomUUID(),
          },
        ],
  )
  const [deletedIdeas, setDeletedIdeas] = useState<string[]>([])
  const [file, setFile] = useState<string | null>(ideation?.logo || null)
  const [fileLoading, setFileLoading] = useState<boolean>(false)

  const fileTypes = useMemo(() => ['JPG', 'PNG', 'JPEG', 'SVG'], [])

  const onFileUpload = useCallback(
    async (fileOrFiles: File | File[]) => {
      try {
        const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles
        setFileLoading(true)
        const uploadFile = await uploadDocumentToAWS({file, id: initID})
        await updateIdeationMutation.mutateAsync({
          ideationData: {logo: uploadFile.fileName},
          ideationId,
        })
        setFile(uploadFile.fileName)
        setFileLoading(false)
      } catch {
        setFileLoading(false)
      }
    },
    [updateIdeationMutation, ideationId, initID],
  )

  const removeIdeationLogo = useCallback(async () => {
    try {
      setFileLoading(true)
      await updateIdeationMutation.mutateAsync({
        ideationData: {logo: null},
        ideationId,
      })
      setFile(null)
      setFileLoading(false)
    } catch {
      setFileLoading(false)
    }
  }, [updateIdeationMutation, ideationId])

  const handleChange = useCallback(
    (name: string, value: string) => {
      if (name === 'name') setTitle(value)
      else setIntroduction(value)
      debeounced400(async () => {
        try {
          await updateIdeationMutation.mutateAsync({
            ideationData:
              name === 'name' ? {name: value || 'Untitled'} : {[name]: value},
            ideationId: ideation?.id || '',
          })
        } catch {
          // error handled by the mutation's onError
        }
      })
    },
    [updateIdeationMutation, ideation],
  )

  const handleCreateQuestion = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuestion(e.target.value)
      debeounced400(async () => {
        try {
          await createIdeationQuestionMutation.mutateAsync({
            ideationId: ideation?.id || '',
            questions: [
              {id: ideation?.questions?.[0]?.id, question: e.target.value},
            ],
            deletedQuestions: [],
          })
        } catch {
          // error handled by the mutation's onError
        }
      })
    },
    [createIdeationQuestionMutation, ideation],
  )

  const questionsSwitch = useCallback(async () => {
    setAllowIdeas(!allowIdeas)
    try {
      await updateIdeationMutation.mutateAsync({
        ideationData: {allowIdeas: !allowIdeas},
        type: 'allowIdeas',
        ideationId: ideation?.id || '',
      })
    } catch {
      // error handled by the mutation's onError
    }
  }, [allowIdeas, updateIdeationMutation, ideation])

  const ideasSwitch = useCallback(async () => {
    setAllowDefaultIdeas(!allowDefaultIdeas)
    try {
      await updateIdeationMutation.mutateAsync({
        ideationData: {allowDefaultIdeas: !allowDefaultIdeas},
        type: 'allowDefaultIdeas',
        ideationId: ideation?.id || '',
      })
    } catch {
      // error handled by the mutation's onError
    }
  }, [allowDefaultIdeas, updateIdeationMutation, ideation])

  const commentsSwitch = useCallback(async () => {
    setAllowIdeasComments(!allowIdeasComments)
    try {
      await updateIdeationMutation.mutateAsync({
        ideationData: {allowIdeasComments: !allowIdeasComments},
        type: 'allowIdeasComments',
        ideationId: ideation?.id || '',
      })
    } catch {
      // error handled by the mutation's onError
    }
  }, [allowIdeasComments, updateIdeationMutation, ideation])

  const saveNewIdeas = useCallback(
    async (data?: string[]) => {
      try {
        await createIdeationIdeaMutation.mutateAsync({
          ideationId: ideation?.id || '',
          ideas,
          deletedIdeas: data ? data : deletedIdeas,
        })
      } catch {
        // error handled by the mutation's onError
      }
    },
    [createIdeationIdeaMutation, deletedIdeas, ideation, ideas],
  )

  const addIda = useCallback(
    (index: number) => {
      if (ideas.every(idea => idea.text.length)) {
        setIdeas([
          ...ideas.slice(0, index),
          {
            text: '',
            username: `${userFirstName}${userLastName}`,
            id: crypto.randomUUID(),
            focus: true,
          },
          ...ideas.slice(index, ideas.length),
        ])
      }
    },

    [ideas, userFirstName, userLastName],
  )
  const removeIda = useCallback(
    (id: string) => {
      if (ideas.length > 1) {
        setIdeas(ideas.filter(idea => idea.id !== id))
        setDeletedIdeas([...deletedIdeas, id])
        saveNewIdeas([...deletedIdeas, id])
      }
    },
    [ideas, deletedIdeas, saveNewIdeas],
  )

  const handleChangeIdeaTitle = useCallback(
    ({id, text}: {id: string; text: string}) => {
      const updateIdea = ideas.map(idea =>
        idea.id === id ? {...idea, text: text} : idea,
      )
      setIdeas(updateIdea)
    },
    [ideas],
  )

  const changeVoteCount = useCallback(
    async (item: IDropdownItem) => {
      try {
        await updateIdeationMutation.mutateAsync({
          ideationData: {votesPerParticipant: Number(item.value ?? 0)},
          ideationId: ideation?.id || '',
        })
      } catch {
        // error handled by the mutation's onError
      }
    },
    [updateIdeationMutation, ideation],
  )

  return (
    <div className={styles.ideaFlowSetup}>
      <div className={styles.ideaFlowSetupHeader}>
        <div className={styles.ideaFlowSetupHeaderBlock1}>
          <div className={styles.ideaFlowSetupTitleBlock}>
            <label className={styles.ideaFlowSetupTitle}>
              {wt(IDEATION.title)}
            </label>
            <WuInput
              value={title}
              maxLength={120}
              placeholder={`${wt(IDEATION.ideationTitlePlaceholder)}`}
              disabled={ideation?.status === IDEATIONSTATUS.Publish}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('name', e.target.value)
              }
            />
            <div
              className={styles.ideaFlowSetupTitleTextLimit}
            >{`${title?.length}/120`}</div>
            {!title?.length ? (
              <ErrorLabel title={`${wt(IDEATION.title)}`} />
            ) : null}
          </div>

          <div className={styles.ideaFlowSetupTitleBlock}>
            <label className={styles.ideaFlowSetupTitle}>
              {wt(IDEATION.introduction)}
            </label>
            <WuTextarea
              value={introduction}
              maxLength={255}
              placeholder={`${wt(IDEATION.ideationDescPlaceholder)}`}
              className="idea-flow-setup--introduction"
              disabled={ideation?.status === IDEATIONSTATUS.Publish}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleChange('introduction', e.target.value)
              }
            />
            <div
              className={styles.ideaFlowSetupTitleTextLimit}
            >{`${introduction?.length}/255`}</div>
          </div>
        </div>

        <div className={styles.ideaFlowSetupHeaderBlock2}>
          <label className={styles.ideaFlowSetupHeaderBlock2Title}>
            {wt(IDEATION.logoPlaceholder)}
          </label>

          {fileLoading ? (
            <LoadingPage />
          ) : (
            <>
              {file ? (
                <div className={styles.ideaFlowSetupHeaderBlock2ImgBlock}>
                  <img
                    src={`${AWS_FILE_URL}/${file}`}
                    alt="logo"
                    width={'100%'}
                    height={'100%'}
                  />
                </div>
              ) : (
                <FileUploader
                  children={<CustomFileUploader fileTypes={fileTypes} />}
                  multiple={false}
                  disabled={ideation?.status === IDEATIONSTATUS.Publish}
                  handleChange={onFileUpload}
                  name="file"
                  types={fileTypes}
                />
              )}

              {file && (
                <div className={styles.ideaFlowSetupHeaderBlock2File}>
                  <span
                    className={styles.ideaFlowSetupHeaderBlock2FileName}
                  >{`${file}`}</span>
                  {ideation?.status !== IDEATIONSTATUS.Publish && (
                    <WuButton
                      variant="secondary"
                      aria-label={wt(GLOBAL.removeLogo)}
                      onClick={removeIdeationLogo}
                      Icon={<WuIcon icon="wm-close" />}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className={styles.ideaFlowSetupSwitchBlock}>
        <CustomSwitch
          isChecked={allowDefaultIdeas || false}
          handleChange={ideasSwitch}
          disabled={ideation?.status === IDEATIONSTATUS.Publish}
          label={`${wt(IDEATION.promotedIdeas)}`}
        />
      </div>
      <div className={'idea-flow-setup--ideas-block'}>
        <p className={styles.ideaFlowSetupSubTitle}>
          {wt(IDEATION.piDescriptionText)}
        </p>
        {allowDefaultIdeas
          ? ideas?.map((idea, index) => (
              <IdeationSetupIdea
                idea={idea}
                key={idea.id}
                index={index}
                disabled={ideation?.status === IDEATIONSTATUS.Publish}
                addIda={addIda}
                removeIda={removeIda}
                handleChangeIdeaTitle={handleChangeIdeaTitle}
                saveNewIdeas={saveNewIdeas}
              />
            ))
          : null}
      </div>
      <div className={styles.ideaFlowSetupSwitchBlock}>
        <CustomSwitch
          isChecked={allowIdeas || false}
          handleChange={questionsSwitch}
          disabled={ideation?.status === IDEATIONSTATUS.Publish}
          label={`${wt(IDEATION.crowdsourceRequest)}`}
        />
      </div>
      <div className={styles.ideaFlowSetupQuestionBlock}>
        <p className={styles.ideaFlowSetupSubTitle}>
          {wt(IDEATION.crDescriptionText)}
        </p>

        {allowIdeas ? (
          <>
            <WuInput
              disabled={ideation?.status === IDEATIONSTATUS.Publish}
              value={question}
              maxLength={255}
              placeholder={wt(IDEATION.questionPlaceholder)}
              onChange={handleCreateQuestion}
            />
            <div
              className={styles.ideaFlowSetupQuestionTextLimit}
            >{`${question?.length}/255`}</div>
          </>
        ) : null}
      </div>
      <div className={styles.ideaFlowSetupSwitchBlock}>
        <CustomSwitch
          isChecked={allowIdeasComments || false}
          handleChange={commentsSwitch}
          disabled={ideation?.status === IDEATIONSTATUS.Publish}
          label={`${wt(IDEATION.comments)}`}
        />
      </div>
      <p className={styles.ideaFlowSetupSubTitle}>
        {wt(IDEATION.commentsDescriptionText)}
      </p>
      <div className={styles.ideaFlowSetupVoteBlock}>
        <label className={styles.ideaFlowSetupTitle}>
          {wt(IDEATION.votesText)}
        </label>
        <div className={styles.ideaFlowSetupVoteDropdownBlock}>
          <DropdownComponent
            data={votes || []}
            defaultValue={
              ideation?.votesPerParticipant?.toString() || 'Select vote'
            }
            disabled={ideation?.status === IDEATIONSTATUS.Publish}
            select={changeVoteCount}
          />
        </div>
      </div>
    </div>
  )
}
